import { api } from './api';

export interface LLDProgressData {
 id: string;
 userId: string;
 problemId: string;
 mode: 'learn' | 'practice' | 'interview';
 isCompleted: boolean;
 completedAt?: string;
 entities: object[];
 responsibilities: Record<string, object>;
 relationships: object[];
 umlDiagram: object;
 codeFiles: Record<string, object[]>;
 activeLanguage: string;
 requirements?: object[];
 brief?: { title: string; description: string };
 sectionStatus: Record<string, string>;
 sectionVisitHistory: { section: string; timestamp: number }[];
 timeSpentPerSection: Record<string, number>;
 totalTimeSpent: number;
 timerTotalSeconds?: number;
 timerRemainingSeconds?: number;
 hintsRevealed: Record<string, number>;
 solutionsRevealed: string[];
 layoutPreset: string;
 panelVisibility: Record<string, boolean>;
 panelSizes: Record<string, number>;
 lastSavedAt?: string;
 createdAt: string;
 updatedAt: string;
}

export interface SaveProgressRequest {
 mode: 'learn' | 'practice' | 'interview';
 isCompleted?: boolean;
 entities?: object[];
 responsibilities?: Record<string, object>;
 relationships?: object[];
 umlDiagram?: object;
 codeFiles?: Record<string, object[]>;
 activeLanguage?: string;
 requirements?: object[];
 brief?: { title: string; description: string };
 sectionStatus?: Record<string, string>;
 sectionVisitHistory?: { section: string; timestamp: number }[];
 timeSpentPerSection?: Record<string, number>;
 totalTimeSpent?: number;
 timerTotalSeconds?: number;
 timerRemainingSeconds?: number;
 hintsRevealed?: Record<string, number>;
 solutionsRevealed?: string[];
 layoutPreset?: string;
 panelVisibility?: Record<string, boolean>;
 panelSizes?: Record<string, number>;
}

export interface LLDProgressSummary {
 totalProblems: number;
 completedProblems: number;
 inProgressProblems: number;
 completedByDifficulty: Record<string, number>;
 totalByDifficulty: Record<string, number>;
 totalTimeSpent: number;
 recentActivity: {
  problemId: string;
  title: string;
  mode: string;
  lastSavedAt: string;
 }[];
}

class LLDProgressService {
 /**
  * Get saved workspace state for a specific problem.
  * Returns null if no progress exists.
  * Requires JWT authentication.
  */
 async getProgress(problemId: string, mode?: 'learn' | 'practice' | 'interview'): Promise<LLDProgressData | null> {
  try {
   const query = mode ? `?mode=${encodeURIComponent(mode)}` : '';
   return await api.get(`/lld-practice/progress/${problemId}${query}`);
  } catch (error: any) {
   if (error.status === 404) {
    return null;
   }
   throw error;
  }
 }

 /**
  * Save/update workspace state for a problem (auto-save).
  * Uses upsert behavior — creates if not exists, updates if exists.
  * Requires JWT authentication.
  */
 async saveProgress(problemId: string, data: SaveProgressRequest): Promise<LLDProgressData> {
  return await api.put(`/lld-practice/progress/${problemId}`, data);
 }

 /**
  * Get overall progress summary (completed count, stats by difficulty).
  * Requires JWT authentication.
  */
 async getProgressSummary(): Promise<LLDProgressSummary> {
  return await api.get('/lld-practice/progress/summary');
 }

 /**
  * Reset progress for a specific problem.
  * Requires JWT authentication.
  */
 async resetProgress(problemId: string): Promise<void> {
  return await api.delete(`/lld-practice/progress/${problemId}`);
 }
}

export const lldProgressService = new LLDProgressService();
export default lldProgressService;
